import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useAlertsCount } from '../hooks/useAlertsCount';
import { toast } from 'react-hot-toast';
import {
  Home,
  Settings,
  Activity,
  FileText,
  LogOut,
  User,
  Sun,
  Battery,
  Zap,
  Clock,
  Play,
  Pause,
  RefreshCw,
  Calculator,
  Menu,
  X,
  Bell
} from 'lucide-react';
import apiService from '../services/api';
import InverterControlToggle from './InverterControlToggle';
import WebSocketStatus from './WebSocketStatus';
import AutoRefreshStatus from './AutoRefreshStatus';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { selectedDataId } = useData();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [schedulerStatus, setSchedulerStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [updatingButton, setUpdatingButton] = useState<'recalculate' | 'trigger' | null>(null);
  const { count: unacknowledgedAlertsCount, hasAlerts } = useAlertsCount();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };


  useEffect(() => {
    const fetchSchedulerStatus = async () => {
      try {
        const status = await apiService.getSchedulerStatus();
        setSchedulerStatus(status);
      } catch (error) {
        console.error('Error fetching scheduler status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedulerStatus();
    // Refresh scheduler status every 30 seconds
    const interval = setInterval(fetchSchedulerStatus, 30000);
    return () => clearInterval(interval);
  }, []);


  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Live', href: '/live', icon: Zap },
    { name: 'Services', href: '/services', icon: Activity },
    { name: 'Alerts', href: '/alerts', icon: Bell },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Logs', href: '/logs', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 mr-3"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              <div className="flex items-center space-x-2">
                <Sun className="h-8 w-8 text-solar-500" />
                <Battery className="h-8 w-8 text-primary-600" />
                <Zap className="h-8 w-8 text-solar-400" />
              </div>
            </div>

            
            <div className="flex items-center space-x-4">
              <WebSocketStatus />
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-gray-400" />
                <button
                  onClick={() => navigate('/settings')}
                  className="text-sm text-gray-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {user?.username}
                </button>
              </div>
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center justify-center space-x-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <LogOut className="h-5 w-5" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Hidden on mobile, visible on lg+ */}
        <nav className="hidden lg:block w-64 bg-white shadow-sm border-r border-gray-200 min-h-screen">
          <div className="p-4">
            <nav className="space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </div>
                    {item.name === 'Alerts' && hasAlerts && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-orange-500 rounded-full">
                        {unacknowledgedAlertsCount > 99 ? '99+' : unacknowledgedAlertsCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Scheduler Status Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-3">
                <Clock className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium text-gray-700">Background Schedule</h3>
              </div>
              
              {loading ? (
                <div className="text-xs text-gray-500">Loading...</div>
              ) : schedulerStatus?.error ? (
                <div className="text-xs text-red-500">{schedulerStatus.error}</div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    {schedulerStatus?.scheduler_running ? (
                      <Play className="h-3 w-3 text-green-500" />
                    ) : (
                      <Pause className="h-3 w-3 text-red-500" />
                    )}
                    <span className="text-xs text-gray-600">
                      {schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'}
                    </span>
                  </div>
                  
                  {schedulerStatus?.jobs?.map((job: any) => (
                    <div key={job.id} className="text-xs text-gray-500 pl-5">
                      <div className="font-medium">
                        {job.name === 'update_services' ? 'Data Collection' : 
                         job.name === 'update_all_users_services_data' ? 'User Data Update' :
                         job.name.replace(/^.*?\.<locals>\./, '').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </div>
                      {job.next_run_time && (
                        <div className="text-gray-400">
                          Next: {new Date(job.next_run_time).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}


            </div>

            {/* Control Buttons Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="space-y-2">
                <button
                   onClick={async () => {
                     setUpdatingButton('trigger');
                     const startTime = Date.now();
                     try {
                       await apiService.triggerSchedulerJob();
                       // Refresh scheduler status after triggering
                       const status = await apiService.getSchedulerStatus();
                       setSchedulerStatus(status);
                     } catch (error) {
                       console.error('Error triggering scheduler job:', error);
                     } finally {
                       // Ensure animation runs for at least 1 second
                       const elapsedTime = Date.now() - startTime;
                       const remainingTime = Math.max(0, 1000 - elapsedTime);
                       setTimeout(() => {
                         setUpdatingButton(null);
                       }, remainingTime);
                     }
                   }}
                   disabled={updatingButton !== null}
                   className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   {updatingButton === 'trigger' ? (
                     <span className="animate-spin h-3 w-3 border-b-2 border-white rounded-full inline-block"></span>
                   ) : (
                     <RefreshCw className="h-3 w-3" />
                   )}
                   <span>{updatingButton === 'trigger' ? 'Triggering...' : 'Trigger Refresh'}</span>
                 </button>

                <button
                  onClick={async () => {
                    if (!selectedDataId) {
                      alert('Please select a data entry first');
                      return;
                    }
                    setUpdatingButton('recalculate');
                    try {
                      await apiService.recalculateForecast(selectedDataId);
                      // Invalidate and immediately refetch relevant queries to update charts and schedules
                      await Promise.all([
                        queryClient.refetchQueries({ queryKey: ['chartData'] }),
                        queryClient.invalidateQueries({ queryKey: ['dataEntries'] }),
                        queryClient.refetchQueries({ queryKey: ['batteryControlSchedules'] }),
                        queryClient.refetchQueries({ queryKey: ['batteryControlScheduleRecommendations'] }),
                      ]);
                      toast.success('Schedules recalculated successfully!');
                    } catch (error: any) {
                      console.error('Error recalculating forecast:', error);
                      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to recalculate forecast';
                      toast.error(errorMsg);
                    } finally {
                      setUpdatingButton(null);
                    }
                  }}
                  disabled={updatingButton !== null}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingButton === 'recalculate' ? (
                    <span className="animate-spin h-3 w-3 border-b-2 border-white rounded-full inline-block"></span>
                  ) : (
                    <Calculator className="h-3 w-3" />
                  )}
                  <span>{updatingButton === 'recalculate' ? 'Recalculating...' : 'Recalc Schedules'}</span>
                </button>


                {/* Inverter Control Enable Toggle */}
                <InverterControlToggle />

                {/* Auto-refresh Status - Development only */}
                {process.env.NODE_ENV === 'development' && <AutoRefreshStatus />}
              </div>
            </div>

          </div>
        </nav>

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Mobile menu */}
            <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4">
                {/* Navigation */}
                <nav className="space-y-2 mb-8">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </div>
                        {item.name === 'Alerts' && hasAlerts && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-orange-500 rounded-full">
                            {unacknowledgedAlertsCount > 99 ? '99+' : unacknowledgedAlertsCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>

                {/* Inverter Control Enable Toggle - Mobile */}
                <div className="mb-6 pt-6 border-t border-gray-200">
                  <InverterControlToggle />
                </div>


              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 